import React from "react"
import { observer } from "mobx-react-lite"

import type { Token } from "@/domains/core"
import type { WithdrawStore } from "@/domains/lock-page/withdraw-store"
import { Card } from "@/components/card"
import { Separator } from "@/components/separator"
import { cn } from "@/domains/library"
import { WithdrawStateButtonsMap } from "@/domains/lock-page/components/card/withdraw-card/composables/withdraw-buttons/withdraw-state-buttons-map"
import { WithdrawStateComponentMap } from "@/domains/lock-page/components/card/withdraw-card/composables/withdraw-panels/withdraw-state-component-map"
import { WithdrawProgressIndicator } from "@/domains/lock-page/components/card/withdraw-card/composables/withdraw-steps/withdraw-progress-indicator"

export interface WithdrawCardProps {
  store: WithdrawStore
  governanceToken: Token
  icon?: React.ReactElement
  onCancel: () => void
  onReset: () => void
  onRetryReset: () => void
  onRetryWithdraw: () => void
  onWithdraw: () => void
}

export const WithdrawCard = observer(
  ({
    store,
    governanceToken,
    icon,
    onCancel,
    onReset,
    onRetryReset,
    onRetryWithdraw,
    onWithdraw,
  }: WithdrawCardProps) => {
    if (!store) {
      console.error("WithdrawCard: store is undefined")
      return null
    }

    return (
      <Card.Container
        className={cn("flex w-full flex-col gap-6", "rounded-lg border border-p400-light bg-p400 p-4 lg:p-6")}
      >
        <Card.Header>
          {icon && (
            <Card.HeaderIconContainer>
              {React.cloneElement(icon, { className: "text-t200", size: 24 })}
            </Card.HeaderIconContainer>
          )}

          <div className="flex w-full flex-col">
            <Card.HeaderTitle>Withdraw</Card.HeaderTitle>
            <Card.HeaderDescription>Follow our interactive guide</Card.HeaderDescription>
          </div>
        </Card.Header>
        <Separator
          className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
  after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
  after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
          decorative
        />
        <WithdrawProgressIndicator store={store} className="mb-4" />
        <div className="mb-8 px-8">
          <WithdrawStateComponentMap store={store} governanceTokenSymbol={governanceToken.symbol} />
        </div>

        <WithdrawStateButtonsMap
          store={store}
          onCancel={onCancel}
          onReset={onReset}
          onRetryReset={onRetryReset}
          onRetryWithdraw={onRetryWithdraw}
          onWithdraw={onWithdraw}
        />
      </Card.Container>
    )
  },
)
